import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { MyElIrregSanabiliEntity, MyElOsservazioniRespPrecRevEntity } from '../../../models/interfaces/revisione.interface';
import { isEqual } from 'lodash-es';
import { toNum } from '@shared/helpers/math.helper';
import { CONFCOOPERATIVE_PEC_DOMAIN, DATE_NOT_ALLOWED, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { conclusioniCkHelper, getConclusioniControl, getConclusioniGroup, hasOsservazioniRespPrecRev } from './helpers/conclusioni-form.helper';
import { isAfter, parseISO } from 'date-fns';
import { formPaths } from '@shared/constants/form-paths.const';

export const conclusioniCoreRules: FormGroupRules = {
    my_CkIrregSanabili: (form: FormGroup) => {
        const conclusioniGroup = getConclusioniGroup(form);
        const my_CkIrregSanabiliControl = getConclusioniControl(form, 'my_CkIrregSanabili');
        const my_ElIrregSanabili: MyElIrregSanabiliEntity[] = getFormArray(conclusioniGroup, 'my_IrregSanabili.my_ElIrregSanabili').value;
        const showError = isNilOrEmpty(my_CkIrregSanabiliControl.value) || (my_CkIrregSanabiliControl.value === '1' && !my_ElIrregSanabili?.length);
        const errors = my_CkIrregSanabiliControl.errors ? my_CkIrregSanabiliControl.errors : {};

        addRemoveError(showError, errors, 'my_CkIrregSanabili', {
            status: "Campo obbligatorio. In caso di risposta affermativa, inserire almeno un'irregolarità sanabile nell'elenco",
        });

        my_CkIrregSanabiliControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_ggDiffida: (form: FormGroup) => {
        const my_ggDiffidaControl = getConclusioniControl(form, 'my_ggDiffida');
        const my_CkProposte: string = getConclusioniControl(form, 'my_CkProposte').value;
        const my_IdAssegnazione: string = getControl(form, 'my_IdAssegnazione').value;
        const errors = my_ggDiffidaControl.errors ? my_ggDiffidaControl.errors : {};

        addRemoveError(
            (toNum(my_ggDiffidaControl.value) < 30 || toNum(my_ggDiffidaControl.value) > 90) && my_CkProposte === '3' && my_IdAssegnazione !== '286394',
            errors,
            'my_ggDiffidaRange',
            {
                status: 'I giorni di diffida non possono essere inferiori a 30 nè superiori a 90',
            }
        );

        addRemoveError(isNilOrEmpty(my_ggDiffidaControl.value) && my_CkProposte === '3', errors, 'my_ggDiffida', {
            status: REQUIRED_STATUS,
        });

        my_ggDiffidaControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DescIrregSanabili: (form: FormGroup) => {
        const conclusioniGroup = getConclusioniGroup(form);
        const my_ElIrregSanabiliFormArray = getFormArray(conclusioniGroup, 'my_IrregSanabili.my_ElIrregSanabili');
        const my_CkIrregSanabili: string = getConclusioniControl(form, 'my_CkIrregSanabili').value;

        my_ElIrregSanabiliFormArray.controls.forEach((group: FormGroup) => {
            const my_DescIrregSanabiliControl = getControl(group, 'my_DescIrregSanabili');
            const errors = my_DescIrregSanabiliControl.errors ? my_DescIrregSanabiliControl.errors : {};

            addRemoveError(isNilOrEmpty(my_DescIrregSanabiliControl.value) && my_CkIrregSanabili === '1', errors, 'my_DescIrregSanabili', {
                status: REQUIRED_STATUS,
            });

            my_DescIrregSanabiliControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_TelefonoRevisore: (form: FormGroup) => {
        recapitoRevisoreHelper(form, 'my_TelefonoRevisore');
    },

    my_CellulareRevisore: (form: FormGroup) => {
        recapitoRevisoreHelper(form, 'my_CellulareRevisore');
    },

    my_OraPrevistaAccertamento: (form: FormGroup) => {
        conclusioniCkHelper(form, 'my_OraPrevistaAccertamento', 'my_CkProposte', 3);
    },

    my_LuogoPrevistoAccertamento: (form: FormGroup) => {
        conclusioniCkHelper(form, 'my_LuogoPrevistoAccertamento', 'my_CkProposte', 3);
    },

    my_CkSanzioniAccessorieRilevazione: (form: FormGroup) => {
        const conclusioniGroup = getConclusioniGroup(form);
        const my_ElSanzioniAccessorieRilevazioneFormArray = getFormArray(conclusioniGroup, 'my_SanzioniAccessorieRilevazione.my_ElSanzioniAccessorieRilevazione');
        const my_CkProposte: string = getConclusioniControl(form, 'my_CkProposte').value;

        my_ElSanzioniAccessorieRilevazioneFormArray.controls.forEach((group: FormGroup) => {
            const CkSanzioniControl = getControl(group, 'my_CkSanzioniAccessorieRilevazione');
            const CkSanzioni: string = CkSanzioniControl.value;
            const errors = CkSanzioniControl.errors ? CkSanzioniControl.errors : {};

            addRemoveError(CkSanzioni === '1' && my_CkProposte !== '2', errors, 'CkSanzioni', {
                status: 'Scelta non consentita. Contattare il capo area',
            });

            CkSanzioniControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_AltraPECRevisore: (form: FormGroup) => {
        const my_AltraPECRevisoreControl = getConclusioniControl(form, 'my_AltraPECRevisore');
        const my_AltraPECRevisore: string = my_AltraPECRevisoreControl.value;
        const errors = my_AltraPECRevisoreControl.errors ? my_AltraPECRevisoreControl.errors : {};

        addRemoveError(my_AltraPECRevisore.includes(CONFCOOPERATIVE_PEC_DOMAIN), errors, 'my_AltraPECRevisore', {
            status: 'Indicare altro indirizzo',
        });

        my_AltraPECRevisoreControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkPresaVisOsservPrecRev: (form: FormGroup) => {
        const conclusioniGroup = getConclusioniGroup(form);
        const my_CkPresaVisOsservPrecRevControl = getConclusioniControl(form, 'my_CkPresaVisOsservPrecRev');
        const my_elOsservazioniRespPrecRev: MyElOsservazioniRespPrecRevEntity[] = getFormArray(conclusioniGroup, 'my_OsservazioniRespPrecRev.my_elOsservazioniRespPrecRev').value;
        const hasOsservazioni = hasOsservazioniRespPrecRev(my_elOsservazioniRespPrecRev);
        const errors = my_CkPresaVisOsservPrecRevControl.errors ? my_CkPresaVisOsservPrecRevControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CkPresaVisOsservPrecRevControl.value) && hasOsservazioni, errors, 'my_CkPresaVisOsservPrecRev', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(my_CkPresaVisOsservPrecRevControl.value === '0' && hasOsservazioni, errors, 'my_CkPresaVisOsservPrecRev', {
            status: 'Errore',
        });

        my_CkPresaVisOsservPrecRevControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkNominaLiq: (form: FormGroup) => {
        const my_CkNominaLiqControl = getConclusioniControl(form, 'my_CkNominaLiq');
        const my_CkProvvedimendoDi: string = getConclusioniControl(form, 'my_CkProvvedimendoDi').value;
        const my_CkProposte: string = getConclusioniControl(form, 'my_CkProposte').value;
        const errors = my_CkNominaLiqControl.errors ? my_CkNominaLiqControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CkNominaLiqControl.value) && toNum(my_CkProvvedimendoDi) === 3 && toNum(my_CkProposte) === 2, errors, 'my_CkNominaLiq', {
            status: REQUIRED_STATUS,
        });

        my_CkNominaLiqControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkProvvedimendoDi: (form: FormGroup) => {
        const my_CkProvvedimendoDiControl = getConclusioniControl(form, 'my_CkProvvedimendoDi');
        const my_CkProposte: string = getConclusioniControl(form, 'my_CkProposte').value;
        const errors = my_CkProvvedimendoDiControl.errors ? my_CkProvvedimendoDiControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CkProvvedimendoDiControl.value) && toNum(my_CkProposte) === 2, errors, 'my_CkProvvedimendoDi', { status: REQUIRED_STATUS });

        my_CkProvvedimendoDiControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_ckNominaLiqAssSoci: (form: FormGroup) => {
        const schedaPropostaGroup = getGroup(form, `${formPaths.conclusioni}.my_SchedaPropostaSostLiquidCancelRegImpre`);
        const my_CkProvvedimendoDi: string = getConclusioniControl(form, 'my_CkProvvedimendoDi').value;
        const my_ckNominaLiqAssSociControl = getControl(schedaPropostaGroup, 'my_ckNominaLiqAssSoci');
        const my_ckNominaLiqAutGiud: string = getControl(schedaPropostaGroup, 'my_ckNominaLiqAutGiud').value;
        const errors = my_ckNominaLiqAssSociControl.errors ? my_ckNominaLiqAssSociControl.errors : {};

        addRemoveError(
            isNilOrEmpty(my_ckNominaLiqAssSociControl.value) && isNilOrEmpty(my_ckNominaLiqAutGiud) && toNum(my_CkProvvedimendoDi) === 4,
            errors,
            'my_ckNominaLiqAssSoci',
            {
                status: REQUIRED_STATUS,
            }
        );

        my_ckNominaLiqAssSociControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_ckNominaLiqAutGiud: (form: FormGroup) => {
        const schedaPropostaGroup = getGroup(form, `${formPaths.conclusioni}.my_SchedaPropostaSostLiquidCancelRegImpre`);
        const my_CkProvvedimendoDi: string = getConclusioniControl(form, 'my_CkProvvedimendoDi').value;
        const my_ckNominaLiqAssSoci: string = getControl(schedaPropostaGroup, 'my_ckNominaLiqAssSoci').value;
        const my_ckNominaLiqAutGiudControl = getControl(schedaPropostaGroup, 'my_ckNominaLiqAutGiud');
        const errors = my_ckNominaLiqAutGiudControl.errors ? my_ckNominaLiqAutGiudControl.errors : {};

        addRemoveError(
            isNilOrEmpty(my_ckNominaLiqAutGiudControl.value) && isNilOrEmpty(my_ckNominaLiqAssSoci) && toNum(my_CkProvvedimendoDi) === 4,
            errors,
            'my_ckNominaLiqAutGiud',
            {
                status: REQUIRED_STATUS,
            }
        );

        my_ckNominaLiqAutGiudControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_descNominaLiq: (form: FormGroup) => {
        const schedaPropostaGroup = getGroup(form, `${formPaths.conclusioni}.my_SchedaPropostaSostLiquidCancelRegImpre`);
        const my_CkProvvedimendoDi: string = getConclusioniControl(form, 'my_CkProvvedimendoDi').value;
        const my_descNominaLiqControl = getControl(schedaPropostaGroup, 'my_descNominaLiq');
        const my_ckNominaLiqAssSoci: boolean = getControl(schedaPropostaGroup, 'my_ckNominaLiqAssSoci').value;
        const my_ckNominaLiqAutGiud: boolean = getControl(schedaPropostaGroup, 'my_ckNominaLiqAutGiud').value;
        const errors = my_descNominaLiqControl.errors ? my_descNominaLiqControl.errors : {};

        addRemoveError(
            (isNilOrEmpty(my_descNominaLiqControl.value) && my_ckNominaLiqAssSoci && toNum(my_CkProvvedimendoDi) === 4) ||
                (isNilOrEmpty(my_descNominaLiqControl.value) && my_ckNominaLiqAutGiud && toNum(my_CkProvvedimendoDi) === 9),
            errors,
            'my_descNominaLiq',
            { status: REQUIRED_STATUS }
        );

        my_descNominaLiqControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataCompilaz: (form: FormGroup) => {
        const my_DataCompilazControl = getConclusioniControl(form, 'my_DataCompilaz');
        const anno2 = toNum(getControl(form, `${formPaths.datiIdentificativi}.my_Anno2`).value);
        const errors = my_DataCompilazControl.errors ? my_DataCompilazControl.errors : {};

        addRemoveError(isNilOrEmpty(my_DataCompilazControl.value), errors, 'my_DataCompilaz', { status: REQUIRED_STATUS });

        addRemoveError(isAfter(parseISO(my_DataCompilazControl.value), new Date(2079, 5, 6)), errors, 'my_DataCompilazNonConsentita', { status: DATE_NOT_ALLOWED });

        addRemoveError(isAfter(parseISO(my_DataCompilazControl.value), new Date(anno2 + 1, 2, 31)), errors, 'my_DataCompilazMarzo', {
            status: 'La revisione non può terminare dopo il mese di Marzo',
        });

        my_DataCompilazControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataScadenzaDiffida: (form: FormGroup) => {
        const my_DataScadenzaDiffidaControl = getConclusioniControl(form, 'my_DataScadenzaDiffida');
        const anno2 = toNum(getControl(form, `${formPaths.datiIdentificativi}.my_Anno2`).value);
        const my_IdAssegnazione: string = getControl(form, 'my_IdAssegnazione').value;
        const errors = my_DataScadenzaDiffidaControl.errors ? my_DataScadenzaDiffidaControl.errors : {};

        addRemoveError(isAfter(parseISO(my_DataScadenzaDiffidaControl.value), new Date(anno2 + 1, 4, 2)) && my_IdAssegnazione !== '286394', errors, 'my_DataScadenzaDiffida', {
            status: 'La scadenza diffida non deve essere successiva al 2 Maggio',
        });

        my_DataScadenzaDiffidaControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const recapitoRevisoreHelper = (form: FormGroup, targetPath: string) => {
    const targetControl = getConclusioniControl(form, targetPath);
    const my_TelefonoRevisore: string = getConclusioniControl(form, 'my_TelefonoRevisore').value;
    const my_CellulareRevisore: string = getConclusioniControl(form, 'my_CellulareRevisore').value;
    const my_CkProposte: string = getConclusioniControl(form, 'my_CkProposte').value;
    const showError = isNilOrEmpty(my_TelefonoRevisore) && isNilOrEmpty(my_CellulareRevisore) && my_CkProposte === '3';
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(showError, errors, targetPath, {
        status: 'Specificare un numero di recapito',
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
